import { Segmented } from 'antd';
import OpterraPhoenixLogo from 'assets/images/OpterraPhoenixLogo.png';
import Notification from 'components/common/Notification';
import {
  FARM_DIAGNOSTICS_ROTATION_NO_PADDOCKS_MESSAGE,
  FARM_DIAGNOSTICS_ROTATION_SOURCE_GUIDANCE,
  FARM_DIAGNOSTICS_ROTATION_VIEW_CHART_LABEL,
  FARM_DIAGNOSTICS_ROTATION_VIEW_TABLE_LABEL,
} from 'constants/uiConstants';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IFarm } from 'interfaces/IFarm';
import type { IInput } from 'interfaces/IInput';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'redux/Store';
import { setRotationTimeRange } from '../../../../redux/slices/InsightsSlice';
import WorkspaceEmptyState from '../WorkspaceEmptyState';
import FarmDiagnosticsTimeRangeControl from './FarmDiagnosticsTimeRangeControl';
import FarmYearInputsPanel from './FarmYearInputsPanel';
import RotationEfficiencyChart from './RotationEfficiencyChart';
import RotationEfficiencyTable from './RotationEfficiencyTable';
import { useRotationEfficiencyData } from './useRotationEfficiencyData';

type ViewMode = 'table' | 'chart';

const VIEW_MODE_OPTIONS: { value: ViewMode; label: string }[] = [
  { value: 'table', label: FARM_DIAGNOSTICS_ROTATION_VIEW_TABLE_LABEL },
  { value: 'chart', label: FARM_DIAGNOSTICS_ROTATION_VIEW_CHART_LABEL },
];

interface RotationEfficiencyViewProps {
  /** Org-wide, already fetched. */
  inputs: IInput[];
  farms: IFarm[];
  /** Already resolved to the tree's parent farm for Paddock/Plan selections, or null for Org/Crop — see useFarmsNavSync.ts. */
  selectedFarmId: string | null;
  orgName: string;
  gUnits: GUnit[];
}

/**
 * Rotation Efficiency — the Farm/Org-level paddock comparison lens. Unlike
 * the three per-paddock lenses, this one needs no paddock selected in the
 * tree; it shows every paddock in the resolved Farm or Org scope side by
 * side, each with its own aggregated figures over the selected date range —
 * never a blended Farm/Org total row. Owns its own `rotationTimeRange`
 * (independent of the per-paddock lenses' `farmDiagnosticsTimeRange`, since
 * the two operate at different scopes).
 */
const RotationEfficiencyView: React.FC<RotationEfficiencyViewProps> = ({
  inputs,
  farms,
  selectedFarmId,
  orgName,
  gUnits,
}) => {
  const dispatch = useDispatch();
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const rotationTimeRange = useSelector(
    (state: RootState) => state.insightsReducer.rotationTimeRange
  );
  const {
    scope,
    minYear,
    maxYear,
    sharedWindowYears,
    rows,
    farmsInScope,
  } = useRotationEfficiencyData(inputs, farms, selectedFarmId, orgName, gUnits);

  if (scope.paddocks.length === 0) {
    return <WorkspaceEmptyState message={FARM_DIAGNOSTICS_ROTATION_NO_PADDOCKS_MESSAGE} />;
  }

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-6">
      <FarmDiagnosticsTimeRangeControl
        minYear={minYear}
        maxYear={maxYear}
        value={rotationTimeRange}
        onChange={(value) => dispatch(setRotationTimeRange(value))}
      />

      {farmsInScope.map(({ farmId, farmName }, index) => (
        <FarmYearInputsPanel
          key={farmId}
          farmId={farmId}
          years={sharedWindowYears}
          farmName={farmName}
          previousFarmId={farmsInScope[index - 1]?.farmId}
        />
      ))}

      <Notification
        type="primary"
        className="w-fit"
        closable={false}
        icon={
          <img
            src={OpterraPhoenixLogo}
            alt="Logo"
            className="mt-1 h-4 w-auto brightness-0 invert"
          />
        }
      >
        <span className="text-sm">
          {FARM_DIAGNOSTICS_ROTATION_SOURCE_GUIDANCE}
        </span>
      </Notification>

      <Segmented<ViewMode>
        className="w-fit"
        options={VIEW_MODE_OPTIONS}
        value={viewMode}
        onChange={setViewMode}
      />

      {viewMode === 'table' ? (
        <RotationEfficiencyTable rows={rows} />
      ) : (
        <RotationEfficiencyChart rows={rows} />
      )}
    </div>
  );
};

export default RotationEfficiencyView;
